(() => {
  'use strict';

  const MODAL_SELECTOR = '[data-zv-advies-modal]';
  const TRIGGER_SELECTOR = 'a[href="#zv-advies-popup"], [data-zv-advice-popup], a[href*="/pages/adviesgesprek"]';
  const WIDGET_SCRIPT = 'https://homezerotech.github.io/Widget/Production/embed.js';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let widgetPromise = null;


  const styledWidgetRoots = new WeakSet();

  function forceOnlineButtonColors(scope) {
    if (!scope || !scope.querySelectorAll) return;

    const candidates = [];
    if (scope.nodeType === 1 && scope.matches && scope.matches('button')) {
      candidates.push(scope);
    }
    candidates.push(...scope.querySelectorAll('button'));

    candidates.forEach((button) => {
      const label = (button.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (!label.includes('vind mijn thuisbatterij')) return;

      button.setAttribute('data-zv-online-submit', 'true');
      button.style.setProperty('color', '#071900', 'important');
      button.style.setProperty('-webkit-text-fill-color', '#071900', 'important');

      button.querySelectorAll('*').forEach((element) => {
        element.style.setProperty('color', '#071900', 'important');
        element.style.setProperty('-webkit-text-fill-color', '#071900', 'important');
        if (element.getAttribute && element.getAttribute('fill')) {
          element.style.setProperty('fill', '#071900', 'important');
        }
        if (element.getAttribute && element.getAttribute('stroke')) {
          element.style.setProperty('stroke', '#071900', 'important');
        }
      });
    });

    scope.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        watchWidgetRoot(element.shadowRoot);
      }
    });
  }

  function watchWidgetRoot(root) {
    if (!root || styledWidgetRoots.has(root)) return;
    styledWidgetRoots.add(root);
    forceOnlineButtonColors(root);

    const observer = new MutationObserver(() => forceOnlineButtonColors(root));
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }

  function styleOnlineWidgetButtons() {
    document.querySelectorAll('.zv-advice-online-widget').forEach((widget) => {
      watchWidgetRoot(widget);
      widget.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) watchWidgetRoot(element.shadowRoot);
      });
    });
  }

  function loadWidgetScript() {
    if (window.customElements && customElements.get('hz-embed')) {
      return Promise.resolve();
    }

    if (widgetPromise) return widgetPromise;

    widgetPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${WIDGET_SCRIPT}"]`);
      if (existing) {
        if (existing.dataset.zvLoaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', () => {
          existing.dataset.zvLoaded = 'true';
          styleOnlineWidgetButtons();
          resolve();
        }, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = WIDGET_SCRIPT;
      script.defer = true;
      script.dataset.zvAdviceWidgetScript = 'true';
      script.addEventListener('load', () => {
        script.dataset.zvLoaded = 'true';
        styleOnlineWidgetButtons();
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return widgetPromise;
  }

  function initializeInlineWidgets() {
    if (document.querySelector('[data-zv-advice-inline] hz-embed')) {
      loadWidgetScript().catch(() => {});
    }
  }

  function initAdviceModal() {
    const modal = document.querySelector(MODAL_SELECTOR);
    if (!modal || modal.dataset.zvAdviceReady === 'true') return;
    modal.dataset.zvAdviceReady = 'true';

    const panel = modal.querySelector('[data-zv-advies-panel]');
    const closeButtons = modal.querySelectorAll('[data-zv-advies-close]');
    const root = modal.querySelector('[data-zv-advice-root]');
    const screens = root ? root.querySelectorAll('[data-zv-advice-screen]') : [];
    const feedback = modal.querySelector('[data-zv-advice-contact-feedback]');
    const successFeedback = modal.querySelector('[data-zv-advice-contact-feedback="success"]');
    let lastFocused = null;
    let closeTimer = 0;
    let successCloseTimer = 0;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    function getFocusable() {
      return panel
        ? [...panel.querySelectorAll(focusableSelector)].filter((element) => !element.hidden && element.offsetParent !== null)
        : [];
    }

    let viewTransitionTimer = 0;
    let viewTransitioning = false;

    function clearViewAnimationStyles(screen) {
      if (!screen) return;
      screen.style.position = '';
      screen.style.inset = '';
      screen.style.width = '';
      screen.style.height = '';
      screen.style.opacity = '';
      screen.style.transform = '';
      screen.style.transition = '';
      screen.style.visibility = '';
      screen.style.pointerEvents = '';
    }

    function focusView(view) {
      window.setTimeout(() => {
        const activeScreen = root && root.querySelector(`[data-zv-advice-screen="${view}"]`);
        const target = activeScreen && activeScreen.querySelector('button, input, a[href], textarea, [tabindex]:not([tabindex="-1"])');
        if (target) target.focus();
      }, reducedMotion.matches ? 0 : 90);
    }

    function showView(view, focus = true) {
      if (!root) return;

      const targetScreen = root.querySelector(`[data-zv-advice-screen="${view}"]`);
      const currentScreen = [...screens].find((screen) => !screen.hidden);
      if (!targetScreen) return;

      root.dataset.zvAdviceView = view;

      if (view === 'online') {
        loadWidgetScript()
          .then(() => {
            styleOnlineWidgetButtons();
            [80, 240, 700, 1500].forEach((delay) => {
              window.setTimeout(() => styleOnlineWidgetButtons(), delay);
            });
            window.setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
          })
          .catch(() => {});
      }

      if (!currentScreen || currentScreen === targetScreen || reducedMotion.matches) {
        screens.forEach((screen) => {
          const active = screen === targetScreen;
          screen.hidden = !active;
          screen.setAttribute('aria-hidden', active ? 'false' : 'true');
          clearViewAnimationStyles(screen);
          if (active) screen.scrollTop = 0;
        });
        root.style.height = '';
        root.style.transition = '';
        root.classList.remove('is-view-transitioning');
        viewTransitioning = false;
        if (focus) focusView(view);
        return;
      }

      if (viewTransitioning) return;
      viewTransitioning = true;
      window.clearTimeout(viewTransitionTimer);

      const direction = view === 'choice' ? -1 : 1;

      targetScreen.hidden = false;
      targetScreen.setAttribute('aria-hidden', 'false');
      targetScreen.scrollTop = 0;
      targetScreen.style.position = 'absolute';
      targetScreen.style.inset = '0';
      targetScreen.style.width = '100%';
      targetScreen.style.height = '100%';
      targetScreen.style.visibility = 'hidden';
      targetScreen.style.pointerEvents = 'none';

      targetScreen.style.visibility = 'visible';
      currentScreen.style.position = 'absolute';
      currentScreen.style.inset = '0';
      currentScreen.style.width = '100%';
      currentScreen.style.height = '100%';
      currentScreen.style.pointerEvents = 'none';

      root.classList.add('is-view-transitioning');

      currentScreen.style.opacity = '1';
      currentScreen.style.transform = 'translateX(0)';
      currentScreen.style.transition = 'none';
      targetScreen.style.opacity = '0';
      targetScreen.style.transform = `translateX(${direction * 28}px)`;
      targetScreen.style.transition = 'none';

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          currentScreen.style.transition = 'opacity .22s ease, transform .34s cubic-bezier(.16,1,.3,1)';
          targetScreen.style.transition = 'opacity .28s ease .03s, transform .36s cubic-bezier(.16,1,.3,1)';

          currentScreen.style.opacity = '0';
          currentScreen.style.transform = `translateX(${-direction * 20}px)`;
          targetScreen.style.opacity = '1';
          targetScreen.style.transform = 'translateX(0)';
        });
      });

      viewTransitionTimer = window.setTimeout(() => {
        currentScreen.hidden = true;
        currentScreen.setAttribute('aria-hidden', 'true');
        targetScreen.hidden = false;
        targetScreen.setAttribute('aria-hidden', 'false');

        clearViewAnimationStyles(currentScreen);
        clearViewAnimationStyles(targetScreen);
        root.classList.remove('is-view-transitioning');
        viewTransitioning = false;

        if (focus) focusView(view);
      }, 390);
    }

    function openModal(view = 'choice') {
      window.clearTimeout(closeTimer);
      lastFocused = document.activeElement;
      modal.classList.remove('is-closing');
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      const scrollbarGap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--zv-advies-scrollbar-gap', `${scrollbarGap}px`);
      document.body.classList.add('zv-advies-modal-open');
      showView(view, false);

      window.setTimeout(() => {
        const first = panel && panel.querySelector('[data-zv-advies-close]');
        if (first) first.focus();
      }, reducedMotion.matches ? 0 : 120);
    }

    function closeModal() {
      if (!modal.classList.contains('is-open')) return;
      window.clearTimeout(successCloseTimer);
      window.clearTimeout(viewTransitionTimer);
      viewTransitioning = false;
      screens.forEach((screen) => clearViewAnimationStyles(screen));
      root.style.height = '';
      root.style.transition = '';
      root.classList.remove('is-view-transitioning');
      modal.classList.remove('is-open');
      modal.classList.add('is-closing');
      modal.setAttribute('aria-hidden', 'true');

      closeTimer = window.setTimeout(() => {
        modal.classList.remove('is-closing');
        document.body.classList.remove('zv-advies-modal-open');
        document.documentElement.style.removeProperty('--zv-advies-scrollbar-gap');
        if (!feedback) showView('choice', false);
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      }, reducedMotion.matches ? 0 : 360);
    }

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(TRIGGER_SELECTOR);
      if (trigger && !trigger.closest(MODAL_SELECTOR)) {
        event.preventDefault();
        event.stopPropagation();
        openModal('choice');
        return;
      }

      const navigation = event.target.closest('[data-zv-advice-go]');
      if (navigation && navigation.closest(MODAL_SELECTOR)) {
        event.preventDefault();
        showView(navigation.dataset.zvAdviceGo || 'choice');
      }
    }, true);

    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeModal();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (!modal.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    if (feedback) {
      openModal('quick');
      window.setTimeout(() => feedback.focus(), reducedMotion.matches ? 0 : 160);

      if (successFeedback) {
        successCloseTimer = window.setTimeout(() => {
          closeModal();
        }, 5000);
      }
    }
  }

  function init() {
    initializeInlineWidgets();
    initAdviceModal();
    styleOnlineWidgetButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
